// Pantalla de CREAR CONTRASEÑA — el destino de los enlaces de correo
// (invitación y "¿Olvidaste tu contraseña?"). Porta mostrarSetPass() de
// app/app.js (~línea 261) al diseño del login de la beta: mismo .login-split /
// .login-caja, para que se sienta parte del mismo flujo y no otra pantalla.
//
// El enlace del correo ya dejó una sesión temporal puesta (supabase-js canjea
// el hash al arrancar), así que updateUser cambia la contraseña DE ESA persona.
import { useState, type CSSProperties, type FormEvent } from "react";
import { supa } from "./lib/supabase";
import { publico } from "./lib/rutas";
import BotonTema from "./components/BotonTema";

const turno = (i: number) => ({ "--i": i }) as CSSProperties;

// Traduce los errores de updateUser. Supabase contesta en inglés y con la
// política de contraseñas del proyecto adentro. OJO: la versión anterior
// mapeaba cualquier "at least" a "demasiado corta" — pero el mensaje de
// política ("Password should contain at least one character of each: ...")
// también trae "at least", así que a quien le faltaba un número o un símbolo
// se le decía que era corta: la alargaba y volvía a fallar, sin salida (bug
// real del 1-sep-2026; el vanilla "funcionaba" porque enseña el mensaje crudo
// en inglés). Ahora se dice exactamente qué le falta.
function traducirErrorPassword(mensaje: string): string {
  // Política de composición: el mensaje enumera los juegos de caracteres
  // requeridos, tal cual ("abcdefghijklmnopqrstuvwxyz, ABC..., 0123..., !@#...").
  // Se detecta cada juego por su corrida literal — sin /i, que las corridas de
  // minúsculas y MAYÚSCULAS solo se distinguen por caja.
  if (/contain at least one/i.test(mensaje)) {
    const falta: string[] = [];
    if (mensaje.includes("abcdefghijklmnopqrstuvwxyz")) falta.push("una letra minúscula");
    if (mensaje.includes("ABCDEFGHIJKLMNOPQRSTUVWXYZ")) falta.push("una letra MAYÚSCULA");
    if (mensaje.includes("0123456789")) falta.push("un número");
    if (mensaje.includes("!@#")) falta.push("un símbolo (por ejemplo ! # $ %)");
    return falta.length
      ? "A la contraseña le falta al menos: " + falta.join(", ") + "."
      : "La contraseña necesita combinar letras, números y símbolos.";
  }
  const corta = mensaje.match(/at least (\d+) characters/i);
  if (corta) return `La contraseña debe tener al menos ${corta[1]} caracteres.`;
  if (/known to be weak|easy to guess|pwned/i.test(mensaje))
    return "Esa contraseña es demasiado común (aparece en listas de contraseñas filtradas). Usa una menos predecible.";
  if (/different from the old|same password/i.test(mensaje))
    return "La contraseña nueva debe ser distinta a la anterior.";
  return "No se pudo guardar: " + mensaje;
}

export default function CrearPassword({
  invitacion,
  email,
  onListo,
}: {
  /** true = invitación (primera contraseña), false = recuperación. */
  invitacion: boolean;
  email: string;
  onListo: () => void;
}) {
  const [pass1, setPass1] = useState("");
  const [pass2, setPass2] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [verPass, setVerPass] = useState(false);

  async function guardar(e: FormEvent) {
    e.preventDefault();
    if (guardando) return;
    setError(null);
    if (pass1.length < 8) {
      setError("Mínimo 8 caracteres.");
      return;
    }
    if (pass1 !== pass2) {
      setError("Las contraseñas no coinciden.");
      return;
    }
    setGuardando(true);
    const { error: err } = await supa.auth.updateUser({ password: pass1 });
    setGuardando(false);
    if (err) {
      setError(traducirErrorPassword(err.message));
      return;
    }
    // Se limpia el hash del enlace: si no, al recargar volvería a caer aquí.
    history.replaceState(null, "", location.pathname + location.search);
    onListo();
  }

  return (
    <div className="login-split">
      <div className="login-marca" aria-hidden="true">
        <div className="blob b1"></div>
        <div className="blob b2"></div>
        <div className="blob b3"></div>
        <div className="marca-contenido">
          <div className="marca-kex">KEX</div>
          <div className="marca-radar-grande">R A D A R</div>
          <div className="marca-raya"></div>
          <p className="marca-frase">
            El radar de Kitchen Experts. Leads, ventas, marketing y servicio,
            en un solo lugar.
          </p>
        </div>
      </div>

      <div className="login-lado">
        <div className="login-tema">
          <BotonTema />
        </div>
        <form className="login-caja" onSubmit={guardar}>
          <div className="login-marca-app entra" style={turno(0)}>
            <img className="login-logo" src={publico("logo-kex.png")} alt="Kitchen Experts" />
            <span className="app-chip">Radar</span>
          </div>
          <h1 className="entra" style={turno(0)}>
            {invitacion ? "Crea tu contraseña" : "Cambia tu contraseña"}
          </h1>
          <p className="login-sub entra" style={turno(1)}>
            {invitacion
              ? "Define la contraseña con la que entrarás al Radar"
              : "Escribe la contraseña nueva"}
            {email ? (
              <>
                {" para "}
                <b>{email}</b>
              </>
            ) : null}
            .
          </p>

          <div className="campo entra" style={turno(2)}>
            <label htmlFor="pass1">Nueva contraseña (mínimo 8 caracteres)</label>
            <div className="campo-control">
              <input
                id="pass1"
                type={verPass ? "text" : "password"}
                autoComplete="new-password"
                minLength={8}
                required
                value={pass1}
                onChange={(e) => setPass1(e.target.value)}
              />
              <button
                type="button"
                className="ver-pass"
                onClick={() => setVerPass((v) => !v)}
                aria-label={verPass ? "Ocultar contraseña" : "Mostrar contraseña"}
              >
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
                  <circle cx="12" cy="12" r="3" />
                  {verPass && <path d="M4 4l16 16" />}
                </svg>
              </button>
            </div>
          </div>

          <div className="campo entra" style={turno(3)}>
            <label htmlFor="pass2">Confírmala</label>
            <input
              id="pass2"
              type={verPass ? "text" : "password"}
              autoComplete="new-password"
              minLength={8}
              required
              value={pass2}
              onChange={(e) => setPass2(e.target.value)}
            />
          </div>

          <button className="btn btn-entrar entra" style={turno(4)} type="submit" disabled={guardando}>
            {guardando ? <span className="spinner" aria-label="Guardando" /> : "Guardar y entrar"}
          </button>

          {error && <div className="error">{error}</div>}
        </form>
      </div>
    </div>
  );
}
